import { hasValidCoordinates } from '~/modules/seo/util/professional-location-schema';
import type { SearchCoordinate, SearchLocation } from '../type/api';
import { toAggregationSearchLocation } from './to-aggregation-search-location';

/** Inputs the place picker and GraphQL both use for a geographic point. */
type CoordinateInput = string | number | null | undefined;

/**
 * A location payload that may still carry the picker's `latitude` / `longitude`
 * fields, or an already-built Search Service `coordinate`.
 */
export type LocationWithPickerCoords = SearchLocation & {
    latitude?: CoordinateInput;
    longitude?: CoordinateInput;
};

/**
 * Parse a latitude/longitude pair into the Search Service's coordinate shape.
 * GraphQL returns them as nullable strings (`"0.0000000"` for ungeocoded rows);
 * the filter panel writes them as URL query numbers. The pair is only usable
 * once it parses to finite, in-range numbers away from the `(0,0)` default.
 */
export function toSearchCoordinate(
    latitude?: CoordinateInput,
    longitude?: CoordinateInput,
): SearchCoordinate | undefined {
    const lat = toCoordinateString(latitude);
    const lon = toCoordinateString(longitude);
    if (lat == null || lon == null) {
        return undefined;
    }
    const coordinates = {
        Latitude: Number(lat),
        Longitude: Number(lon),
    };
    if (!hasValidCoordinates(coordinates)) {
        return undefined;
    }
    return { lat: coordinates.Latitude, lon: coordinates.Longitude };
}

/**
 * Build the Search Service `searchLocation` payload: name levels plus a ranking
 * coordinate when one is usable. Extra picker fields stay off the request.
 * Coordinates alone are not a location filter — without a name level or slug
 * there is nothing to send.
 */
export function toSearchServiceLocation(
    location: LocationWithPickerCoords | null | undefined,
): SearchLocation | null {
    if (!location) {
        return null;
    }
    const nameLevels = pickNameLevels(location);
    if (Object.keys(nameLevels).length === 0) {
        return null;
    }
    const coordinate =
        location.coordinate ??
        toSearchCoordinate(location.latitude, location.longitude);
    return {
        ...nameLevels,
        ...(coordinate ? { coordinate } : {}),
    };
}

/**
 * Name levels only. Aggregations count results, they never rank them, so
 * ranking-only fields (coordinate, picker lat/lng, county grain) must not
 * travel on count queries or partition the aggregation cache.
 */
export function omitRankingCoordinate(
    location: LocationWithPickerCoords,
): SearchLocation {
    return toAggregationSearchLocation(location);
}

/** Place identity fields that define the result set, never ranking. */
function pickNameLevels(location: LocationWithPickerCoords): SearchLocation {
    const nameLevels = toAggregationSearchLocation(location);
    return location.slug ? { ...nameLevels, slug: location.slug } : nameLevels;
}

/** Blank strings must not become `0` (Number('') === 0). */
function toCoordinateString(value: CoordinateInput): string | undefined {
    if (value == null) {
        return undefined;
    }
    const asString = typeof value === 'number' ? String(value) : value;
    const trimmed = asString.trim();
    return trimmed === '' ? undefined : trimmed;
}
